import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import type { FieldValues, UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { captainBillingKeys } from "./query-keys";
import { captainBillingUrl } from "./url";
import { applyBillingError } from "../components/editors/billingErrors";
import type { CaptainBillingAccount, CorrectBalanceRequest, SetMonthlyCapRequest, TopUpRequest } from "../types";

import { instance } from "@/lib/axios/instance";
import type { ProblemDetails } from "@/lib/helpers/handleProblemDetailsError";
import useToast from "@/lib/hooks/useToast";

function useCaptainBillingMutation<TRequest extends Pick<CaptainBillingAccount, "clientId">>(
  send: (request: TRequest) => Promise<unknown>,
  successKey: string,
) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation<unknown, AxiosError<ProblemDetails>, TRequest>({
    mutationFn: send,
    onSuccess: (_, { clientId }) => queryClient.invalidateQueries({ queryKey: captainBillingKeys.byClient(clientId) }),
  });

  const save = <T extends FieldValues>(request: TRequest, setError: UseFormSetError<T>, onDone: () => void) =>
    mutate(request, {
      onSuccess: () => {
        showToast(t(successKey));
        onDone();
      },
      onError: (error) => {
        const message = applyBillingError(error, setError, t);
        if (message) showToast(message, "error");
      },
    });

  return { save, isPending };
}

export const useSetMonthlyCap = () =>
  useCaptainBillingMutation<SetMonthlyCapRequest>(
    ({ clientId, ...body }) => instance.put(captainBillingUrl(clientId, "Cap"), body),
    "captainBilling.toasts.capSaved",
  );

export const useTopUpTokens = () =>
  useCaptainBillingMutation<TopUpRequest>(
    ({ clientId, ...body }) => instance.post(captainBillingUrl(clientId, "TopUps"), body),
    "captainBilling.toasts.toppedUp",
  );

export const useCorrectBalance = () =>
  useCaptainBillingMutation<CorrectBalanceRequest>(
    ({ clientId, ...body }) => instance.post(captainBillingUrl(clientId, "Corrections"), body),
    "captainBilling.toasts.corrected",
  );
